import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Package, Truck, CheckCircle2, X, IndianRupee, ShieldCheck, MapPin, User2, RotateCcw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { useAuth } from "../../contexts/AuthContext";
import Tracking from "../../components/Tracking";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;
const STATUS_PILL = {
    created: "bg-slate-100 text-slate-600",
    payment_pending: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    packed: "bg-blue-100 text-blue-700",
    dispatched: "bg-indigo-100 text-indigo-700",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-600",
};

// v3F — Backward transitions: previous → current state pairs admin can revert.
const BACKWARD_TARGETS = {
    packed: "paid",
    dispatched: "packed",
    delivered: "dispatched",
};

const Content = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPharmacistReadOnly = user?.role === "pharmacist";
    const [order, setOrder] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dispatchOpen, setDispatchOpen] = useState(false);
    const [dispatchMode, setDispatchMode] = useState("auto"); // "auto" or "manual"
    const [dispatchForm, setDispatchForm] = useState({ courier_name: "Delhivery", awb_no: "" });
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [refundOpen, setRefundOpen] = useState(false);
    const [refundForm, setRefundForm] = useState({ amount: "", reason: "" });
    const [transitionOpen, setTransitionOpen] = useState(false);
    const [transitionReason, setTransitionReason] = useState("");
    const [reinstateOpen, setReinstateOpen] = useState(false);
    const [reinstateForm, setReinstateForm] = useState({ reason: "", reverse_wallet_refund: true });

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/orders/${id}`);
            setOrder(r.data);
        } catch {
            toast.error("Order not found");
            navigate("/admin/orders");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const callAction = async (path, body = null, successMsg = "Done") => {
        setBusy(true);
        try {
            await api.post(`/admin/orders/${id}/${path}`, body || {});
            toast.success(successMsg);
            await load();
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Action failed");
            return false;
        } finally { setBusy(false); }
    };

    if (loading || !order) return <div className="h-[60vh] skeleton rounded-2xl" />;

    const dispatchSubmit = async () => {
        if (dispatchMode === "manual") {
            if (!dispatchForm.awb_no.trim() || !dispatchForm.courier_name.trim()) {
                toast.error("AWB and courier are required for manual dispatch");
                return;
            }
            if (await callAction("dispatch", dispatchForm, "Order dispatched (manual)")) setDispatchOpen(false);
        } else {
            // Auto path — send empty body, backend will call Shiprocket
            if (await callAction("dispatch", {}, "Order dispatched via Shiprocket")) setDispatchOpen(false);
        }
    };
    const cancelSubmit = async () => {
        if (!cancelReason.trim()) { toast.error("Reason required"); return; }
        if (await callAction("cancel", { reason: cancelReason }, "Order cancelled")) setCancelOpen(false);
    };
    const refundSubmit = async () => {
        const amt = parseFloat(refundForm.amount);
        if (!(amt > 0) || !refundForm.reason.trim()) { toast.error("Amount and reason are required"); return; }
        if (await callAction("refund", { amount: amt, reason: refundForm.reason }, `₹${amt} refunded to wallet`)) setRefundOpen(false);
    };
    const transitionSubmit = async () => {
        if (transitionReason.trim().length < 4) { toast.error("Please provide a reason (min 4 chars)"); return; }
        const target = BACKWARD_TARGETS[order.status];
        if (!target) { toast.error("No backward transition available"); return; }
        if (await callAction("transition", { target_status: target, reason: transitionReason }, `Reverted to ${target}`)) {
            setTransitionOpen(false);
            setTransitionReason("");
        }
    };
    const reinstateSubmit = async () => {
        if (reinstateForm.reason.trim().length < 4) { toast.error("Please provide a reason (min 4 chars)"); return; }
        if (await callAction("reinstate", reinstateForm, "Order reinstated")) {
            setReinstateOpen(false);
            setReinstateForm({ reason: "", reverse_wallet_refund: true });
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-order-detail">
            <div className="flex items-center justify-between">
                <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-700">
                    <ArrowLeft className="h-4 w-4" /> All orders
                </Link>
                <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase ${STATUS_PILL[order.status]}`} data-testid="order-status-pill">{order.status}</span>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
                <div className="space-y-5">
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <p className="label-uppercase">Order</p>
                                <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900" data-testid="order-no">{order.order_no}</h2>
                                <p className="text-xs text-slate-500">Placed {new Date(order.created_at).toLocaleString("en-IN")}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-semibold tabular-nums text-slate-900">{inr(order.amounts?.total)}</p>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400">Net charged</p>
                            </div>
                        </div>
                        {/* Items */}
                        <ul className="mt-5 divide-y divide-slate-100">
                            {(order.items || []).map((it, i) => (
                                <li key={i} className="flex items-center gap-3 py-3">
                                    <img src={it.image || "/pills-logo.jpg"} alt="" className="h-12 w-12 rounded-lg object-cover ring-1 ring-slate-200" />
                                    <div className="min-w-0 flex-1">
                                        <p className="line-clamp-1 text-sm font-medium text-slate-900">{it.name}</p>
                                        <p className="text-xs text-slate-500">{it.brand} {it.pack_size && `· ${it.pack_size}`} · qty {it.qty}</p>
                                        {it.prescription_required && <span className="mt-0.5 inline-flex items-center gap-0.5 rounded bg-brand-50 px-1 py-0.5 text-[9px] font-semibold text-brand-700"><ShieldCheck className="h-2.5 w-2.5" /> Rx</span>}
                                    </div>
                                    <p className="text-sm font-medium tabular-nums">{inr(it.line_total)}</p>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Activity</p>
                        <ol className="mt-3 space-y-2.5">
                            {(order.history || []).map((h, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-xs">
                                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
                                    <div className="flex-1">
                                        <p className="font-medium text-slate-900">{h.event.replace(/_/g, " ")}</p>
                                        {h.note && <p className="text-slate-500">{h.note}</p>}
                                        <p className="text-[10px] uppercase text-slate-400">{h.actor} · {new Date(h.at).toLocaleString("en-IN")}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>
                </div>

                <aside className="space-y-5">
                    {/* Customer */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="order-customer">
                        <p className="label-uppercase">Customer</p>
                        <div className="mt-3 flex items-start gap-2.5">
                            <User2 className="mt-0.5 h-4 w-4 text-slate-400" />
                            <div className="text-sm">
                                <p className="font-medium text-slate-900">{order.customer?.name}</p>
                                <p className="text-xs text-slate-500">{order.customer?.email}</p>
                                <p className="text-xs text-slate-500">{order.customer?.phone}</p>
                                {order.customer?.id && (
                                    <Link to={`/admin/users/${order.customer.id}`} className="mt-1 inline-block text-[11px] font-medium text-brand-700 hover:underline">View customer →</Link>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Shipping */}
                    {order.shipping_address && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                            <p className="label-uppercase">Ship to</p>
                            <div className="mt-3 flex items-start gap-2.5 text-sm">
                                <MapPin className="mt-0.5 h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="font-medium text-slate-900">{order.shipping_address.label}</p>
                                    <p className="text-xs text-slate-600">{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</p>
                                    <p className="text-xs text-slate-600">{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}</p>
                                    <p className="text-xs text-slate-500">{order.shipping_address.phone}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Breakdown */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Breakdown</p>
                        <dl className="mt-3 space-y-1.5 text-sm">
                            <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="tabular-nums">{inr(order.amounts?.subtotal)}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">Shipping</dt><dd className="tabular-nums">{inr(order.amounts?.shipping_fee)}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">GST</dt><dd className="tabular-nums">{inr(order.amounts?.tax_gst)}</dd></div>
                            {order.wallet_applied > 0 && (
                                <div className="flex justify-between text-brand-700"><dt>Wallet</dt><dd className="tabular-nums">−{inr(order.wallet_applied)}</dd></div>
                            )}
                            <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold"><dt>Total</dt><dd className="tabular-nums">{inr(order.amounts?.total)}</dd></div>
                            <p className="text-[11px] text-slate-500">Paid by <strong className="text-slate-700">{order.payment?.method || order.payment?.provider}</strong></p>
                        </dl>
                    </div>

                    {/* Actions */}
                    {!isPharmacistReadOnly && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="order-actions">
                            <p className="label-uppercase">Actions</p>
                            <div className="mt-3 flex flex-col gap-2">
                                {order.status === "paid" && (
                                    <button onClick={() => callAction("mark-packed", null, "Marked packed")} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50" data-testid="order-pack-btn">
                                        <Package className="h-4 w-4" /> Mark packed
                                    </button>
                                )}
                                {order.status === "packed" && (
                                    <button onClick={() => setDispatchOpen(true)} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50" data-testid="order-dispatch-btn">
                                        <Truck className="h-4 w-4" /> Dispatch
                                    </button>
                                )}
                                {order.status === "dispatched" && (
                                    <button onClick={() => callAction("mark-delivered", null, "Marked delivered")} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50" data-testid="order-deliver-btn">
                                        <CheckCircle2 className="h-4 w-4" /> Mark delivered
                                    </button>
                                )}
                                {order.dispatch?.awb_no && (
                                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs">
                                        <p className="font-medium text-slate-900">AWB: {order.dispatch.awb_no}</p>
                                        <p className="text-slate-500">{order.dispatch.courier_name}</p>
                                    </div>
                                )}
                                <button onClick={() => setRefundOpen(true)} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50" data-testid="order-refund-btn">
                                    <IndianRupee className="h-3.5 w-3.5" /> Refund to wallet
                                </button>
                                {BACKWARD_TARGETS[order.status] && (
                                    <button
                                        onClick={() => setTransitionOpen(true)}
                                        disabled={busy}
                                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-2 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                                        data-testid="order-transition-back-btn"
                                    >
                                        <Undo2 className="h-3.5 w-3.5" /> Revert to {BACKWARD_TARGETS[order.status]}
                                    </button>
                                )}
                                {order.status === "cancelled" && (
                                    <button
                                        onClick={() => setReinstateOpen(true)}
                                        disabled={busy}
                                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                                        data-testid="order-reinstate-btn"
                                    >
                                        <RotateCcw className="h-4 w-4" /> Reinstate order
                                    </button>
                                )}
                                {["created", "payment_pending", "paid", "packed"].includes(order.status) && (
                                    <button onClick={() => setCancelOpen(true)} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 px-4 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50" data-testid="order-cancel-btn">
                                        <X className="h-3.5 w-3.5" /> Cancel order
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </aside>
            </div>

            {dispatchOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-zinc-900" data-testid="dispatch-modal">
                        <p className="label-uppercase">Dispatch</p>
                        <h2 className="text-lg font-semibold tracking-tight">Pick a courier</h2>
                        <p className="mt-1 text-xs text-slate-500">Auto picks the cheapest courier via Shiprocket. Manual is the fallback if Shiprocket fails or the wallet isn&apos;t recharged.</p>

                        <div className="mt-4 grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setDispatchMode("auto")}
                                className={`rounded-xl border p-3 text-left transition-colors ${dispatchMode === "auto" ? "border-brand-300 bg-brand-50 dark:border-brand-400/40 dark:bg-brand-500/10" : "border-slate-200 dark:border-white/10"}`}
                                data-testid="dispatch-mode-auto"
                            >
                                <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100">Auto (Shiprocket)</p>
                                <p className="mt-0.5 text-[11px] text-slate-500">Creates a real shipment + AWB</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => setDispatchMode("manual")}
                                className={`rounded-xl border p-3 text-left transition-colors ${dispatchMode === "manual" ? "border-brand-300 bg-brand-50 dark:border-brand-400/40 dark:bg-brand-500/10" : "border-slate-200 dark:border-white/10"}`}
                                data-testid="dispatch-mode-manual"
                            >
                                <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100">Manual</p>
                                <p className="mt-0.5 text-[11px] text-slate-500">Enter AWB + courier yourself</p>
                            </button>
                        </div>

                        {dispatchMode === "manual" && (
                            <div className="mt-4 space-y-3">
                                <label className="block">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Courier</span>
                                    <input value={dispatchForm.courier_name} onChange={(e) => setDispatchForm({...dispatchForm, courier_name: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="dispatch-courier" />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">AWB number</span>
                                    <input value={dispatchForm.awb_no} onChange={(e) => setDispatchForm({...dispatchForm, awb_no: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="dispatch-awb" />
                                </label>
                            </div>
                        )}

                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setDispatchOpen(false)} disabled={busy} className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm dark:border-white/10">Cancel</button>
                            <button onClick={dispatchSubmit} disabled={busy} className="flex-1 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50" data-testid="dispatch-confirm">
                                {busy ? "Working..." : (dispatchMode === "auto" ? "Create shipment" : "Confirm dispatch")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {cancelOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="cancel-modal">
                        <p className="label-uppercase text-rose-600">Cancel order</p>
                        <h2 className="text-lg font-semibold tracking-tight">Confirm cancellation</h2>
                        <p className="mt-1 text-xs text-slate-500">Stock will be restored and any payment refunded to the customer&apos;s wallet.</p>
                        <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} placeholder="Reason" className="input-clean !rounded-lg mt-4" data-testid="cancel-reason" />
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setCancelOpen(false)} disabled={busy} className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm">Back</button>
                            <button onClick={cancelSubmit} disabled={busy} className="flex-1 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white" data-testid="cancel-confirm">Cancel order</button>
                        </div>
                    </div>
                </div>
            )}

            {refundOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="refund-modal">
                        <p className="label-uppercase">Refund</p>
                        <h2 className="text-lg font-semibold tracking-tight">Credit customer wallet</h2>
                        <div className="mt-4 space-y-3">
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount (₹)</span>
                                <input type="number" min="1" step="0.01" value={refundForm.amount} onChange={(e) => setRefundForm({...refundForm, amount: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="refund-amount" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Reason</span>
                                <input value={refundForm.reason} onChange={(e) => setRefundForm({...refundForm, reason: e.target.value})} className="input-clean !rounded-lg mt-1" data-testid="refund-reason" />
                            </label>
                        </div>
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setRefundOpen(false)} disabled={busy} className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={refundSubmit} disabled={busy} className="flex-1 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white" data-testid="refund-confirm">Credit wallet</button>
                        </div>
                    </div>
                </div>
            )}

            {transitionOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="transition-modal">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Backward transition</p>
                        <h2 className="text-lg font-semibold tracking-tight">Revert {order.status} → {BACKWARD_TARGETS[order.status]}</h2>
                        <p className="mt-1 text-xs text-slate-500">
                            The customer will be notified of the status change. Audit-logged for compliance.
                        </p>
                        <label className="mt-4 block">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Reason</span>
                            <textarea
                                rows={3}
                                value={transitionReason}
                                onChange={(e) => setTransitionReason(e.target.value)}
                                placeholder="e.g. Courier returned to hub — need to re-pack."
                                className="input-clean !rounded-lg mt-1"
                                data-testid="transition-reason"
                            />
                        </label>
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setTransitionOpen(false)} disabled={busy} className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={transitionSubmit} disabled={busy} className="flex-1 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50" data-testid="transition-confirm">
                                Confirm revert
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reinstateOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="reinstate-modal">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Reinstate cancelled order</p>
                        <h2 className="text-lg font-semibold tracking-tight">Re-open this order</h2>
                        <p className="mt-1 text-xs text-slate-500">
                            Stock will be re-deducted. If a refund was credited on cancel, it will be debited back from the wallet.
                        </p>
                        <label className="mt-4 block">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Reason</span>
                            <textarea
                                rows={3}
                                value={reinstateForm.reason}
                                onChange={(e) => setReinstateForm({ ...reinstateForm, reason: e.target.value })}
                                placeholder="e.g. Customer called back — wants to proceed with the order."
                                className="input-clean !rounded-lg mt-1"
                                data-testid="reinstate-reason"
                            />
                        </label>
                        <label className="mt-3 flex items-center gap-2 text-xs text-slate-700">
                            <input
                                type="checkbox"
                                checked={reinstateForm.reverse_wallet_refund}
                                onChange={(e) => setReinstateForm({ ...reinstateForm, reverse_wallet_refund: e.target.checked })}
                                data-testid="reinstate-reverse-refund"
                            />
                            Reverse the wallet refund (debit customer wallet for the refund amount).
                        </label>
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setReinstateOpen(false)} disabled={busy} className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={reinstateSubmit} disabled={busy} className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="reinstate-confirm">
                                Reinstate order
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminOrderDetail = () => (
    <AdminAuthGuard>
        <AdminLayout title="Order" subtitle="Detail, dispatch and refund">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminOrderDetail;
